import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { resolveAvatarUrl } from '../../utils/resolveAvatarUrl';

/**
 * React mirror of `IncomingCallActivity.AvatarCircleView` (white theme):
 *  - White-filled circle with the contact initial (serif bold 54sp,
 *    #1A1430) when no image is provided, or the image clipped to the
 *    circle otherwise.
 *  - A soft dark drop shadow (rgba(0,0,0,0.55)) sits just below the
 *    circle and pulses while ringing (1800ms, alpha + radius), then
 *    settles once the call is connected.
 */
export const CallAvatar = ({
  name = '',
  imageUrl,
  size = 148,
  ringing = false,
  dataTestId,
}) => {
  const [pulse, setPulse] = useState(0.6);
  const animatingRef = useRef(true);

  useEffect(() => {
    animatingRef.current = ringing;
    if (!ringing) {
      setPulse(0.55);
      return undefined;
    }
    let direction = 1;
    let value = 0.35;
    const minimum = 0.35;
    const maximum = 1;
    const step = 1 / 60; // ~60fps, so a full 0.35→1 sweep ≈ 11 frames ≈ ~1.8s with the delay below
    const interval = window.setInterval(() => {
      value += direction * step * 0.72;
      if (value >= maximum) { value = maximum; direction = -1; }
      if (value <= minimum) { value = minimum; direction = 1; }
      setPulse(value);
    }, 30);
    return () => window.clearInterval(interval);
  }, [ringing]);

  const initial = (name || 'A').trim().charAt(0).toUpperCase() || 'A';
  const resolved = resolveAvatarUrl(imageUrl);
  const shadowAlpha = (90 + 40 * pulse) / 255;

  return (
    <div
      data-testid={dataTestId}
      style={{
        position: 'relative',
        width: size,
        height: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Dark drop shadow — V2: centred under the avatar, tighter blur,
          dimmer alpha, and ~8% smaller than the circle so it reads as a
          soft under-lift instead of a boxed grey halo in the corner. */}
      <motion.div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: size * 0.92,
          height: size * 0.92,
          marginLeft: -(size * 0.92) / 2,
          marginTop: -(size * 0.92) / 2 + 10,
          borderRadius: '50%',
          background: `rgba(0, 0, 0, ${shadowAlpha})`,
          filter: 'blur(10px)',
        }}
      />
      <div
        style={{
          position: 'relative',
          width: size,
          height: size,
          borderRadius: '50%',
          overflow: 'hidden',
          background: '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {resolved ? (
          <img
            src={resolved}
            alt=""
            draggable={false}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <span
            style={{
              fontFamily: "'Inria Serif', serif",
              fontWeight: 700,
              fontSize: Math.round(size * 0.365),
              color: '#1A1430',
              lineHeight: 1,
            }}
          >
            {initial}
          </span>
        )}
      </div>
    </div>
  );
};
